'use client';

import { IconChevronLeftFilled } from '@zillow/constellation-icons/react/filled/icon-chevron-left-filled';
import { IconChevronRightFilled } from '@zillow/constellation-icons/react/filled/icon-chevron-right-filled';
import { Fragment, forwardRef, useContext, useMemo } from 'react';

import { css, cx } from '@/styled-system/css';

import { Box } from '../box';
import { Icon } from '../icon';
import { CalendarRootContext } from './context/calendar-root.context';
import { useCalendarControlsRenderProps } from './hooks/use-calendar-renders';
import { calendarControlsStyles } from './styles/calendar-controls.styles';
import type { CalendarControlsPropsInterface } from './types/calendar-controls.types';

export const CalendarControls = forwardRef<HTMLDivElement, CalendarControlsPropsInterface>(
  (props: CalendarControlsPropsInterface, forwardedRef) => {
    const { calendar } = useContext(CalendarRootContext);

    const { renderYearSelect, renderMonth, renderNextMonth, renderPrevMonth, ...nonRenderProps } =
      useCalendarControlsRenderProps(props);

    const { children, className, css: cssProp, ...rest } = nonRenderProps;

    const styles = css(calendarControlsStyles, cssProp);

    const controls = useMemo(
      () => (
        <Fragment>
          {renderPrevMonth({
            ...calendar?.getPreviousMonthProps(),
            title: 'Previous month',
            children: <Icon size="sm" render={<IconChevronLeftFilled />} />,
          })}
          <Box css={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            {renderMonth({
              'aria-atomic': true,
              'aria-live': 'polite',
              'role': 'region',
              'children': `${calendar?.currentMonth} ${calendar?.currentYear}`,
            })}
            {renderYearSelect({
              ...calendar?.getYearSelectProps(),
              children: calendar?.yearRange?.map((year) => (
                <option key={year} value={year}>
                  {year}
                </option>
              )),
            })}
          </Box>
          {renderNextMonth({
            ...calendar?.getNextMonthProps(),
            title: 'Next month',
            children: <Icon size="sm" render={<IconChevronRightFilled />} />,
          })}
        </Fragment>
      ),
      [renderYearSelect, renderMonth, renderNextMonth, renderPrevMonth, calendar],
    );

    return (
      <div
        ref={forwardedRef}
        role="group"
        className={cx('c11n', styles, className)}
        data-c11n-component="Calendar.Controls"
        data-c11n-version={__C11N_VERSION__}
        {...rest}
      >
        {children || controls}
      </div>
    );
  },
);

CalendarControls.displayName = 'Calendar.Controls';
